// messageCenter/areaInput.js — the field „Bereich“ of a ticket (boss's ticket T212): a text input whose own value filters
// the areas already in use; the list below it is the keyword autocomplete's suggestionMenu. A new area is simply typed
// and stays as it is: it is saved with the ticket and offered from then on. The area stays free text, no scheme.
//
// createAreaInput({ value, source }) → { element (the input), destroy }. Focus shows all areas, typing filters them
// (contains, any case; those that start with the text first), ↑/↓/Enter/Esc and the mouse pick one. Picking sets the
// value and fires `change`, as if it had been typed and the field left. Without a source it is a plain input.
//
// createAreaSource(api) → { list, remember }: the areas, loaded once per page on first need.
import { createDisposers } from '../core/disposers.js';
import { createSuggestionMenu } from '../keywordAutocomplete/suggestionMenu.js';

export function createAreaInput({ value = '', source = null } = {}) {
    const disposers = createDisposers();
    const element = document.createElement('input');
    element.type = 'text';
    element.className = 'topo2-field-textinput topo2-field--bordered';   // bordered: a white field on the white page
    element.value = value;
    if (!source) { return { element, destroy: () => {} }; }

    const menu = createSuggestionMenu({
        onSelect: (item) => {
            element.value = item.label;
            element.dispatchEvent(new Event('change', { bubbles: true }));
        },
    });
    disposers.add(() => menu.destroy());
    menu.element.classList.add('areaInput__menu');   // above the quick report window (modules/messagecenter-form.css)
    let request = 0;

    // source() never rejects (createAreaSource gives [] on a failure), so there is no catch here
    function open(term) {
        const mine = ++request;
        source().then((areas) => {
            if (mine !== request || document.activeElement !== element) { return; }   // newer request, or the field was left
            const rect = element.getBoundingClientRect();
            menu.show(matching(areas, term).map((label) => ({ label })), { left: rect.left, top: rect.top, bottom: rect.bottom }, element);
        });
    }

    const on = (event, fn) => { element.addEventListener(event, fn); disposers.add(() => element.removeEventListener(event, fn)); };
    on('focus', () => open(''));
    on('input', () => open(element.value));
    on('keydown', (e) => {
        if (!menu.isOpen()) {
            if (e.key === 'ArrowDown') { e.preventDefault(); open(element.value); }
            return;
        }
        if (e.key === 'ArrowDown') { e.preventDefault(); menu.highlightNext(); }
        else if (e.key === 'ArrowUp') { e.preventDefault(); menu.highlightPrev(); }
        else if (e.key === 'Enter') { e.preventDefault(); menu.selectHighlighted(); }
        else if (e.key === 'Escape') { e.preventDefault(); menu.hide(); }
    });
    // the menu picks on mousedown with preventDefault, so a pick does not blur the field first
    on('blur', () => { request++; menu.hide(); });

    return { element, destroy: () => disposers.dispose() };
}

/** The areas containing the text (any case), those starting with it first; all for an empty text. */
function matching(areas, term) {
    const t = term.trim().toLowerCase();
    if (t === '') { return areas; }
    const hits = areas.filter((a) => a.toLowerCase().includes(t));
    return [...hits.filter((a) => a.toLowerCase().startsWith(t)), ...hits.filter((a) => !a.toLowerCase().startsWith(t))];
}

/**
 * The areas in use, loaded once per page when first needed (api.listAreas). remember() adds an area saved since, so
 * it is offered without a reload. A failed load gives an empty list (logged) and is tried again next time.
 */
export function createAreaSource(api) {
    let loaded = null;
    const added = [];

    function list() {
        if (!loaded) {
            loaded = api.listAreas()
                .then((response) => {
                    if (!response?.success) { throw new Error(response?.error ?? 'no answer'); }
                    return response.data;
                })
                .catch((e) => {
                    console.error('message center: loading the areas failed', e);
                    loaded = null;   // next time it tries again
                    return [];
                });
        }
        return loaded.then((areas) => [...new Set([...areas, ...added])].sort((a, b) => a.localeCompare(b, 'de')));
    }

    function remember(area) {
        const value = (area ?? '').trim();
        if (value !== '' && !added.includes(value)) { added.push(value); }
    }

    return { list, remember };
}
